import {
  EPDFieldNameEnum,
  EPDSearchFilter,
  PagedEPDSearchOUT,
  PaginatedMaterial2050SearchOUT,
  inventoryEPDsApi,
  materials2050ApiFp,
  productEditorApi,
} from "api-client";
import { SelectedItems } from "features/EPDSearchModal/components/FiltersMenu";
import { makeAutoObservable } from "mobx";
import { BPEStore } from "features/BuildingProductEditor/ProductEditorStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";

export const EPDs_PAGE_SIZE = 25;

class EPDSearchStore {
  selectedFilters: SelectedItems[] = [];
  fetchingEPDsLoading: boolean = false;
  isExternalSearch: boolean = false;
  isOrganisationalSearch: boolean = false;
  EPDs: PagedEPDSearchOUT | PaginatedMaterial2050SearchOUT = {
    count: 0,
    items: [],
  };
  setSelectedFilters(selectedFilters: SelectedItems[]) {
    this.selectedFilters = selectedFilters;
  }

  setFetchingEPDsLoading(fetchingEPDsLoading: boolean) {
    this.fetchingEPDsLoading = fetchingEPDsLoading;
  }

  setIsExternalSearch(isExternalSearch: boolean) {
    this.isExternalSearch = isExternalSearch;
  }

  setIsOrganisationalSearch(isOrganisationalSearch: boolean) {
    this.isOrganisationalSearch = isOrganisationalSearch;
  }

  setEPDs(EPDs: PagedEPDSearchOUT | PaginatedMaterial2050SearchOUT) {
    this.EPDs = EPDs;
  }

  async fetch2050FilterCategories() {
    return await materials2050ApiFp()
      .importerApiV1RoutersMaterials2050ListFilterCategories()
      .then((response) => response.data)
      .catch((error) => console.error(error, "error.fetchFilterCategories"));
  }

  async fetch2050FilterChildren(
    categoryID: string,
    searchTerm: string,
    page?: number,
    pageSize?: number
  ) {
    return await materials2050ApiFp()
      .importerApiV1RoutersMaterials2050SearchFilterOptions(
        categoryID,
        searchTerm,
        page,
        pageSize
      )
      .then((response) => response.data)
      .catch((err) => console.error(err, "Materials2050SearchFilterOptions"));
  }

  async fetchConcoreEPDFilterChildren(
    fieldName: string,
    searchTerm: string,
    page: number,
    pageSize: number
  ) {
    return await productEditorApi()
      .inventoryApiV1RoutersProductEditorGetEpdFilterOptions(
        fieldName as EPDFieldNameEnum,
        searchTerm,
        page,
        pageSize
      )
      .then((response) => response.data)
      .catch((err) => console.error(err, "ProductEditorGetEpdFilterOptions"));
  }

  async fetchEPDs(term: string, page: number, pageSize: number) {
    this.setFetchingEPDsLoading(true);

    const filters = this.selectedFilters
      .filter((f) => f.category.id !== "creator")
      .map((f) => ({
        field: f.category.value,
        values: f.items.map((rec) => rec.id),
      }));

    const { orgID, onlyFromOrganisation } = this.checkEPDCreatorFilter();

    return await productEditorApi()
      .inventoryApiV1RoutersProductEditorSearchEpds(
        {
          term: term ?? "",
          conformity: [],
          database: [],
          data_type: [],
          filtering: filters as EPDSearchFilter[],
          organisation_id: orgID,
          only_from_organisation: onlyFromOrganisation,
        },
        null,
        null,
        page,
        pageSize
      )
      .then((response) => response.data)
      .finally(() => this.setFetchingEPDsLoading(false));
  }

  private checkEPDCreatorFilter() {
    /**
     * Evaluates the "creator" filter and determines:
     * - which organisation ID should be applied to the query
     * - whether results should be restricted to the current organisation
     *
     * Resulting combinations:
     * - Only Concular EPDs:        orgID = null        && onlyFromOrganisation = false
     * - Only current org EPDs:     orgID = {uuid}      && onlyFromOrganisation = true
     * - All accessible EPDs:       orgID = {uuid}      && onlyFromOrganisation = false
     */

    const organisationItems =
      this.selectedFilters.find((f) => f.category.id === "creator")?.items ??
      [];

    const selectedIds = organisationItems.map((item) => item.id);

    const hasCurrent = selectedIds.includes("current");

    const hasConcular = selectedIds.includes("concular");

    const shouldUseCurrentOrg =
      (!hasCurrent && !hasConcular) || // no filter selected
      hasCurrent; // current selected (alone or with concular)

    const orgID = shouldUseCurrentOrg
      ? sidebarMenuStore.currentOrganisation?.id ?? null
      : null;

    const onlyFromOrganisation =
      this.isOrganisationalSearch || (hasCurrent && !hasConcular); // get current org EPDs only

    return { orgID, onlyFromOrganisation };
  }

  async fetch2050EPDs(term?: string, page?: number, pageSize?: number) {
    this.setFetchingEPDsLoading(true);
    const filters = this.selectedFilters.map((f) => ({
      category_key: String(f.category.value),
      ids: f.items.map((rec) => Number(rec.id)),
    }));
    return await materials2050ApiFp()
      .importerApiV1RoutersMaterials2050SearchMaterial2050Epds(
        { search_term: term ?? "", filters: filters },
        page,
        pageSize
      )
      .then((response) => response.data)
      .catch((error) => console.error(error, "SearchMaterial2050Epds"))
      .finally(() => this.setFetchingEPDsLoading(false));
  }

  async fetch2050EPDDetails(epd_id: string) {
    return await materials2050ApiFp()
      .importerApiV1RoutersMaterials2050GetMaterials2050EpdDetails(epd_id)
      .then((response) => response.data)
      .catch((error) => console.error(error, "GetMaterials2050EpdDetails"));
  }

  async fetchEPDDetails(epd_id: string) {
    return await productEditorApi()
      .inventoryApiV1RoutersProductEditorGetEpdDetail(epd_id)
      .then((response) => response.data)
      .catch((error) => console.error(error, "ProductEditorGetEpdDetail"));
  }

  async import2050EPD(epd_id: string) {
    return await materials2050ApiFp()
      .importerApiV1RoutersMaterials2050ImportEpd(epd_id)
      .then((response) => response.data)
      .catch((error) => console.error(error, "Materials2050ImportEpd"));
  }

  handleSelect2050EPD = async (
    epd_id: string,
    datasetIndex: number,
    rowIndex: number
  ) => {
    const res = await epdSearchStore.import2050EPD(epd_id);
    if (res) BPEStore.onSelectEPDRow(res, datasetIndex, rowIndex);
    if (res) BPEStore.onSelectEPDRow(res, datasetIndex, rowIndex);
    return res;
  };

  async deleteEPD(epd_id: string) {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;
    await inventoryEPDsApi()
      .inventoryApiV1RoutersEpdDeleteEpd(epd_id, orgID)
      .catch((error) => console.error(error, "DeleteEpd"));
  }

  reset() {
    this.selectedFilters = [];
    this.isExternalSearch = false;
    this.fetchingEPDsLoading = false;
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: EPDSearchStore;

  static getInstance(): EPDSearchStore {
    if (!EPDSearchStore.instance) {
      EPDSearchStore.instance = new EPDSearchStore();
    }
    return EPDSearchStore.instance;
  }
}

export const epdSearchStore = EPDSearchStore.getInstance();
